import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, Workspace, WorkspaceRole } from '@/types/models'

export const WORKSPACE_STORAGE_KEY = 'workspaceId'

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Returns the stored workspace uuid, or null.
 *
 * Anything that isn't a uuid is discarded rather than returned: sessions that
 * predate the uuid switch have an integer id under this key, and sending it
 * would make every request 400 until the user manually cleared storage.
 */
export function getStoredWorkspaceId(): string | null {
  const raw = localStorage.getItem(WORKSPACE_STORAGE_KEY)
  if (!raw || !UUID_PATTERN.test(raw)) return null
  return raw
}

export function clearStoredWorkspaceId(): void {
  localStorage.removeItem(WORKSPACE_STORAGE_KEY)
}

export const useWorkspaceStore = defineStore('workspace', () => {
  const workspaces         = ref<Workspace[]>([])
  const currentWorkspaceId = ref<string | null>(getStoredWorkspaceId())

  /**
   * Whether `workspaces` reflects a real answer from the server yet.
   *
   * The stored id is untrusted input: it survives in localStorage after the
   * membership behind it is revoked. Until the list loads we cannot tell a
   * valid selection from a stale one, so the router waits on this rather than
   * letting `currentWorkspaceId` alone open the app (LPD-2 AC 17).
   */
  const listLoaded = ref(false)

  const currentWorkspace = computed<Workspace | null>(
    () => workspaces.value.find((w) => w.id === currentWorkspaceId.value) ?? null,
  )

  /** A selection is only trustworthy once the list has confirmed it. */
  const hasValidSelection = computed(() => listLoaded.value && currentWorkspace.value !== null)

  const currentRole = computed<WorkspaceRole | null>(() => currentWorkspace.value?.role ?? null)

  const isWorkspaceAdmin = computed(
    () => currentRole.value === 'owner' || currentRole.value === 'admin',
  )

  /** `client` pivot members are not agency-side; the API 403s the whole staff surface for them. */
  const isClientRole = computed(() => currentRole.value === 'client')

  function setCurrent(id: string) {
    currentWorkspaceId.value = id
    localStorage.setItem(WORKSPACE_STORAGE_KEY, id)
  }

  function clearCurrent() {
    currentWorkspaceId.value = null
    clearStoredWorkspaceId()
  }

  function setWorkspaces(list: Workspace[]) {
    workspaces.value = list
    listLoaded.value = true
    reconcileSelection()
  }

  /**
   * Seed the store from an authenticated user.
   */
  function hydrateFrom(user: User | null) {
    workspaces.value = user?.workspaces ?? []
    listLoaded.value = user?.workspaces !== undefined
    reconcileSelection()
  }

  /**
   * Settle the selection against the loaded list.
   *
   * A selection that is no longer a membership is *discarded*, not silently
   * replaced with another workspace. Quietly swapping tenants under someone who
   * asked for a specific one is how you show them the wrong client's data and
   * have them believe it belongs to the workspace they picked — so we drop it
   * and let the router send them to the selection screen (LPD-2 AC 17).
   *
   * With no prior selection at all there is nothing to betray, so we pick the
   * first workspace and skip a pointless selection screen.
   */
  function reconcileSelection() {
    const stored = currentWorkspaceId.value ?? getStoredWorkspaceId()
    if (stored !== null) {
      if (workspaces.value.some((w) => w.id === stored)) setCurrent(stored)
      else clearCurrent()
      return
    }
    autoSelect()
  }

  /**
   * Re-read the list from the server. Backs AC 8: opening the switcher shows a
   * workspace created or granted in another tab without a manual reload.
   */
  async function refresh(): Promise<void> {
    const { fetchWorkspaces } = await import('@/api/workspaces')
    setWorkspaces(await fetchWorkspaces())
  }

  function autoSelect() {
    const first = workspaces.value[0]
    if (first) setCurrent(first.id)
    else clearCurrent()
  }

  return {
    workspaces,
    currentWorkspaceId,
    currentWorkspace,
    currentRole,
    isWorkspaceAdmin,
    isClientRole,
    listLoaded,
    hasValidSelection,
    setCurrent,
    clearCurrent,
    setWorkspaces,
    hydrateFrom,
    refresh,
  }
})
